import React, { useEffect, useState } from 'react';
import { Box, Typography, Button, TextField, Snackbar, Alert, Paper, Table, TableHead, TableRow, TableCell, TableBody, CircularProgress, Grid, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions, IconButton } from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import AddIcon from '@mui/icons-material/Add';

const emptyForm = {
  name: '',
  type: '',
  file: null
};

const DOCUMENT_TYPES = [
  { value: 'spec', label: 'Specification' },
  { value: 'contract', label: 'Contract' },
  { value: 'design', label: 'Design' },
  { value: 'other', label: 'Other' },
];

const Documents = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [filters, setFilters] = useState({ q: '', type: '', from: '', to: '' });
  const [dialogOpen, setDialogOpen] = useState(false);

  const fetchDocuments = () => {
    setLoading(true);
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
    fetch('/api/documents?' + params.toString())
      .then(res => res.json())
      .then(data => {
        setDocuments(data);
        setLoading(false);
      });
  };

  useEffect(() => { fetchDocuments(); }, [filters]);

  const handleChange = e => {
    const { name, value, files } = e.target;
    setForm(f => ({ ...f, [name]: files ? files[0] : value }));
  };

  const handleFilterChange = e => {
    const { name, value } = e.target;
    setFilters(f => ({ ...f, [name]: value }));
  };

  const handleDialogOpen = () => {
    setForm(emptyForm);
    setDialogOpen(true);
    setError('');
    setSuccess('');
  };
  const handleDialogClose = () => setDialogOpen(false);

  const handleSubmit = async e => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (!form.file) {
      setError('Please select a file');
      return;
    }
    try {
      const fd = new FormData();
      fd.append('name', form.name);
      fd.append('type', form.type);
      fd.append('file', form.file);
      const res = await fetch('/api/documents', {
        method: 'POST',
        body: fd
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to upload document');
      setSuccess('Document uploaded!');
      setForm(emptyForm);
      setDialogOpen(false);
      fetchDocuments();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDelete = async id => {
    if (!window.confirm('Delete this document?')) return;
    setError('');
    setSuccess('');
    try {
      const res = await fetch(`/api/documents/${id}`, { method: 'DELETE' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to delete document');
      setSuccess('Document deleted!');
      fetchDocuments();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDownload = id => {
    window.open(`/api/documents/${id}/download`, '_blank');
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">Documents</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleDialogOpen}>
          Upload Document
        </Button>
      </Box>
      <Grid container spacing={3}>
        <Grid item xs={12} md={12}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Filter Documents</Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
              <TextField label="Keyword" name="q" value={filters.q} onChange={handleFilterChange} size="small" />
              <TextField label="Type" name="type" value={filters.type} onChange={handleFilterChange} size="small" select sx={{ minWidth: 120 }}>
                <MenuItem value="">Type</MenuItem>
                {DOCUMENT_TYPES.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
              </TextField>
              <TextField label="From" name="from" type="date" value={filters.from} onChange={handleFilterChange} size="small" InputLabelProps={{ shrink: true }} />
              <TextField label="To" name="to" type="date" value={filters.to} onChange={handleFilterChange} size="small" InputLabelProps={{ shrink: true }} />
            </Box>
            <Typography variant="h6" sx={{ mb: 2 }}>Documents List</Typography>
            {loading ? <CircularProgress /> : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Uploader</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {documents.map(d => (
                    <TableRow key={d.id}>
                      <TableCell>{d.name}</TableCell>
                      <TableCell>{d.type}</TableCell>
                      <TableCell>{d.uploader}</TableCell>
                      <TableCell>{new Date(d.created_at).toLocaleString()}</TableCell>
                      <TableCell>
                        <IconButton onClick={() => handleDownload(d.id)}><DownloadIcon /></IconButton>
                        <IconButton color="error" onClick={() => handleDelete(d.id)}><DeleteIcon /></IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Paper>
        </Grid>
      </Grid>
      <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>Upload Document</DialogTitle>
        <DialogContent>
          <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField label="Name" name="name" value={form.name} onChange={handleChange} size="small" required />
            <TextField label="Type" name="type" value={form.type} onChange={handleChange} size="small" select required>
              {DOCUMENT_TYPES.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
            </TextField>
            <Button variant="contained" component="label">
              Select File
              <input type="file" name="file" hidden onChange={handleChange} />
            </Button>
            {form.file && <Typography variant="body2">Selected: {form.file.name}</Typography>}
            <Button type="submit" variant="contained">Upload</Button>
            <Button onClick={handleDialogClose} color="secondary">Cancel</Button>
          </Box>
        </DialogContent>
      </Dialog>
      <Snackbar open={!!error} autoHideDuration={4000} onClose={() => setError('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert onClose={() => setError('')} severity="error">{error}</Alert>
      </Snackbar>
      <Snackbar open={!!success} autoHideDuration={3000} onClose={() => setSuccess('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert onClose={() => setSuccess('')} severity="success">{success}</Alert>
      </Snackbar>
    </Box>
  );
};

export default Documents; 